import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  AlertTitle,
  Autocomplete,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  deleteCustomer,
  getAllCountry,
  getCustomer,
} from "../redux/actions/AdminAction";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { GET_CUSTOMER_RESET } from "../Constants/Constants";
import { LOCAL_URL } from "../Constants/global";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#282828",
    color: "#ffffff",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#e8e9eb",
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function createData(
  id,
  created_at,
  customer_name,
  country,
  email,
  cc,
  pccs,
  active,
  report_language
) {
  return {
    id,
    created_at,
    customer_name,
    country,
    email,
    cc,
    pccs,
    active,
    report_language,
  };
}

const getCustomerListFromPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.customers)) return payload.customers;
  if (Array.isArray(payload?.results)) return payload.results;
  if (payload && typeof payload === "object") return Object.values(payload);
  return [];
};

const getCustomerTotalFromPayload = (payload, fallbackTotal) => {
  if (typeof payload?.total === "number") return payload.total;
  if (typeof payload?.total_count === "number") return payload.total_count;
  if (typeof payload?.count === "number") return payload.count;
  if (typeof payload?.meta?.total === "number") return payload.meta.total;
  return fallbackTotal;
};

const normalizeCustomer = (value) =>
  createData(
    value.customer_id,
    value.created_at,
    value.customer_name,
    value.country,
    value.email,
    value.cc,
    value.pccs,
    value.active,
    value.report_language
  );

const formatCustomersForDownload = (customers) =>
  customers.map((customer, index) => ({
    sr_no: index + 1,
    customer_id: customer.id,
    created_at: customer.created_at,
    customer_name: customer.customer_name,
    country: customer.country,
    email: customer.email,
    cc: customer.cc,
    pccs: customer.pccs,
    status: customer.active ? "Active" : "Inactive",
    report_language: customer.report_language || "",
  }));

export default function ManageCustomer(props) {
  const { checkUserPermission } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [row, setRow] = React.useState();
  const [searchType, setSearchType] = React.useState("name");
  const [countrySearchValue, setCountrySearchValue] = React.useState(null);
  const searchQueryRef = React.useRef("");
  const searchInputRef = React.useRef(null);
  const [deleteCustomerId, setDeleteCustoemrId] = React.useState(null);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [pccSyncMessage, setPccSyncMessage] = React.useState("");
  const [dialogOpen, setdialogOpen] = React.useState(false);
  const [downloadLoading, setDownloadLoading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [totalRows, setTotalRows] = React.useState(0);
  const [serverPaginated, setServerPaginated] = React.useState(false);
  const [appliedSearch, setAppliedSearch] = React.useState({
    value: "",
    type: "name",
  });

  const GetCustomers = useSelector((state) => state.GetCustomers);
  const { customersInfo, loading } = GetCustomers;

  const GetAllCountry = useSelector((state) => state.GetAllCountry);
  const { countryInfo, loading: countryLoading } = GetAllCountry;

  const DeleteCustomer = useSelector((state) => state.DeleteCustomer);
  const { success, error } = DeleteCustomer;

  const countryOptions = React.useMemo(() => {
    const countries = Array.isArray(countryInfo)
      ? countryInfo
      : Object.values(countryInfo || {});

    return countries
      .map((country) => country?.name?.trim())
      .filter(Boolean)
      .sort((firstCountry, secondCountry) =>
        firstCountry.localeCompare(secondCountry)
      );
  }, [countryInfo]);

  const handleDialogOpen = () => {
    setdialogOpen(true);
  };

  const handleDialogClose = () => {
    setdialogOpen(false);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOpen(false);
    setPccSyncMessage("");
  };

  const handleDownloadSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setDownloadError("");
  };

  React.useEffect(() => {
    const message = location.state?.pccSyncMessage;
    if (!message) return;

    setPccSyncMessage(message);
    setSnackOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  React.useEffect(() => {
    dispatch(
      getCustomer(
        appliedSearch.value,
        appliedSearch.type,
        page + 1,
        rowsPerPage
      )
    );
  }, [appliedSearch, dispatch, page, rowsPerPage]);

  React.useEffect(() => {
    if (searchType === "country") {
      dispatch(getAllCountry());
    }
  }, [dispatch, searchType]);

  React.useEffect(() => {
    if (customersInfo) {
      const customerList = getCustomerListFromPayload(customersInfo);
      const hasServerPagination =
        !Array.isArray(customersInfo) &&
        (Array.isArray(customersInfo?.data) ||
          Array.isArray(customersInfo?.customers) ||
          Array.isArray(customersInfo?.results));
      const newRows = customerList.map(normalizeCustomer);
      setRow(newRows);
      setServerPaginated(hasServerPagination);
      setTotalRows(getCustomerTotalFromPayload(customersInfo, newRows.length));
    }
  }, [customersInfo]);

  const displayedRows = React.useMemo(() => {
    if (!row?.length) return [];
    if (serverPaginated) return row;

    const firstRowIndex = page * rowsPerPage;
    return row.slice(firstRowIndex, firstRowIndex + rowsPerPage);
  }, [page, row, rowsPerPage, serverPaginated]);

  const handleCustomerSearch = () => {
    const value = (searchQueryRef.current || "").trim();
    setPage(0);
    setAppliedSearch({
      value,
      type: searchType,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatSearchValue = (value, type) => {
    if (!value) return "";

    if (type === "pcc") {
      return value.toUpperCase();
    }

    if (type === "name") {
      return value.toUpperCase();
    }

    return value;
  };

  const fetchCustomersForDownload = async (token) => {
    if (!serverPaginated) {
      return row || [];
    }

    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const downloadRowsPerPage = Math.max(rowsPerPage, 500);
    let nextPage = 1;
    let downloadedCustomers = [];
    let expectedTotal = totalRows;

    while (true) {
      const query = new URLSearchParams({
        page: nextPage,
        per_page: downloadRowsPerPage,
      });

      if (appliedSearch.value) {
        query.set("search_by", appliedSearch.type);
        query.set("search", appliedSearch.value);
      }

      const { data } = await axios.get(
        `${LOCAL_URL}/get_all_customer?${query.toString()}`,
        config
      );
      const customerList = getCustomerListFromPayload(data);
      const downloadedPageRows = customerList.map(normalizeCustomer);

      downloadedCustomers = [...downloadedCustomers, ...downloadedPageRows];
      expectedTotal = getCustomerTotalFromPayload(data, expectedTotal);

      if (
        !downloadedPageRows.length ||
        downloadedCustomers.length >= expectedTotal ||
        downloadedPageRows.length < downloadRowsPerPage
      ) {
        break;
      }

      nextPage += 1;
    }

    return downloadedCustomers;
  };

  const handleDownloadCustomers = async () => {
    if (!row?.length && !totalRows) {
      setDownloadError("No data available to download.");
      return;
    }

    setDownloadLoading(true);
    setDownloadError("");

    try {
      const token = JSON.parse(localStorage.getItem("userInfo"));
      const customersForDownload = await fetchCustomersForDownload(token);

      if (!customersForDownload.length) {
        setDownloadError("No data available to download.");
        return;
      }

      const payload = {
        search_by: appliedSearch.type,
        search_value: appliedSearch.value,
        table_data: formatCustomersForDownload(customersForDownload),
      };

      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      };

      const { data, headers } = await axios.post(
        `${LOCAL_URL}/download_customers_excel`,
        payload,
        config
      );

      const contentDisposition = headers["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i
      );
      const fileName = fileNameMatch?.[1]
        ? fileNameMatch[1].replace(/['"]/g, "").trim()
        : "customers.xlsx";

      const blob = new Blob([data], {
        type:
          headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileUrl);
    } catch (downloadErr) {
      setDownloadError(
        downloadErr?.response?.data?.detail ||
          downloadErr?.message ||
          "Unable to download customer file."
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Container>
        <Box>
          <Box sx={{ margin: 3, fontSize: 25 }}>
            Manage your customers
            <Divider sx={{ marginTop: 2 }}></Divider>
          </Box>
          <Box
            sx={{
              fontSize: 25,
              margin: 3,
              display: "flex",
              flexWrap: "nowrap",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {checkUserPermission(104, "can_add") && (
              <Button
                type="submit"
                size="small"
                onClick={() => {
                  dispatch({ type: GET_CUSTOMER_RESET });
                  navigate("/managecustomer/new");
                }}
                sx={{
                  backgroundColor: "#282828",
                  color: "#ffffff",
                  width: "25vh",
                  borderRadius: 0,
                  fontSize: 16,
                  "&:hover": {
                    backgroundColor: "#282828",
                    color: "#ffffff",
                  },
                }}
              >
                Add new customer
              </Button>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <FormControl size="small" sx={{ minWidth: "20vh" }}>
                <InputLabel id="customer-search-by-label">Search by</InputLabel>
                <Select
                  labelId="customer-search-by-label"
                  id="customer-search-by"
                  value={searchType}
                  label="Search by"
                  disabled={loading}
                  onChange={(e) => {
                    setSearchType(e.target.value);
                    setCountrySearchValue(null);
                    searchQueryRef.current = "";
                    if (searchInputRef.current) {
                      searchInputRef.current.value = "";
                    }
                  }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="pcc">PCC</MenuItem>
                  <MenuItem value="country">Country</MenuItem>
                </Select>
              </FormControl>
              {searchType === "country" ? (
                <Autocomplete
                  size="small"
                  sx={{ width: "22vh" }}
                  options={countryOptions}
                  value={countrySearchValue}
                  autoHighlight
                  loading={countryLoading}
                  disabled={loading}
                  onChange={(event, newValue) => {
                    setCountrySearchValue(newValue);
                    searchQueryRef.current = newValue || "";
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select country"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {countryLoading ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleCustomerSearch();
                        }
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  size="small"
                  sx={{ width: "22vh" }}
                  placeholder="Search"
                  inputRef={searchInputRef}
                  disabled={loading}
                  onChange={(e) => {
                    const formattedValue = formatSearchValue(
                      e.target.value,
                      searchType
                    );
                    searchQueryRef.current = formattedValue;
                    if (formattedValue !== e.target.value) {
                      e.target.value = formattedValue;
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCustomerSearch();
                    }
                  }}
                />
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleCustomerSearch}
                disabled={loading}
                sx={{
                  minWidth: "unset",
                  backgroundColor: "#282828",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#282828",
                    color: "#ffffff",
                  },
                  "&:disabled": {
                    backgroundColor: "#D8D9DA",
                    color: "#6b6b6b",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <SearchIcon fontSize="small" />
                )}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleDownloadCustomers}
                disabled={loading || downloadLoading || !row?.length}
                startIcon={<DownloadIcon fontSize="small" />}
                sx={{
                  backgroundColor: "#282828",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#282828",
                    color: "#ffffff",
                  },
                  "&:disabled": {
                    backgroundColor: "#D8D9DA",
                    color: "#6b6b6b",
                  },
                }}
              >
                {downloadLoading ? "Downloading..." : "Download"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginBottom: "5%",
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            width: "80%",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>No.</StyledTableCell>
                <StyledTableCell>Created at</StyledTableCell>
                <StyledTableCell>Customer Name</StyledTableCell>
                <StyledTableCell>Country</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>CC</StyledTableCell>
                <StyledTableCell>PCCs</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Edit</StyledTableCell>
                <StyledTableCell>Delete</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    colSpan={10}
                    scope="row"
                    style={{
                      textAlign: "center",
                      wordWrap: "break-word",
                      maxWidth: "100%",
                      fontSize: 15,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        minHeight: 52,
                      }}
                    >
                      <CircularProgress size={24} />
                      Loading customers...
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ) : displayedRows?.length > 0 ? (
                displayedRows.map((row, index) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell component="th" scope="row">
                      {page * rowsPerPage + index + 1}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.created_at}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      scope="row"
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "100px",
                      }}
                    >
                      {row.customer_name}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.country}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      scope="row"
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "200px",
                      }}
                    >
                      {row?.email?.trim()?.split(" ")?.join("\n")}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      scope="row"
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "200px",
                      }}
                    >
                      {row.cc?.trim()?.split(" ")?.join("\n")}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "180px",
                      }}
                    >
                      {row.pccs}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "100px",
                      }}
                    >
                      {row.active ? "Active" : "Inactive"}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "500px",
                        textAlign: "left",
                      }}
                    >
                      <Button
                        disabled={
                          checkUserPermission(104, "can_edit") ? false : true
                        }
                        sx={{
                          backgroundColor: "#ffffff",
                          border: "1px solid black",
                          color: "black",
                          fontSize: 12,
                          height: "30px",
                          "&:disabled": {
                            border: 0,
                            backgroundColor: "#D8D9DA",
                          },
                        }}
                        onClick={() => navigate(`/managecustomer/${row.id}`)}
                      >
                        Edit
                      </Button>
                    </StyledTableCell>
                    <StyledTableCell
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "500px",
                        textAlign: "left",
                      }}
                    >
                      <Button
                        disabled={
                          checkUserPermission(104, "can_delete") ? false : true
                        }
                        sx={{
                          backgroundColor: "#d9534f",
                          color: "white",
                          fontSize: 12,
                          height: "30px",
                          "&:hover": {
                            backgroundColor: "#d9534f",
                            color: "white",
                          },
                          "&:disabled": {
                            border: 0,
                            backgroundColor: "#D8D9DA",
                          },
                        }}
                        onClick={() => {
                          setdialogOpen(true);
                          setDeleteCustoemrId(row.id);
                        }}
                      >
                        Delete
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow
                  style={{
                    wordWrap: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  <StyledTableCell
                    component="th"
                    colSpan={10}
                    scope="row"
                    style={{
                      textAlign: "center",
                      wordWrap: "break-word",
                      maxWidth: "100%",
                      fontSize: 15,
                    }}
                  >
                    No data found
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            backIconButtonProps={{
              disabled: loading || page === 0,
            }}
            nextIconButtonProps={{
              disabled:
                loading || page >= Math.ceil(totalRows / rowsPerPage) - 1,
            }}
            SelectProps={{
              disabled: loading,
            }}
          />
        </TableContainer>
        {success && (
          <Snackbar
            open={snackOpen}
            autoHideDuration={5000}
            onClose={handleSnackClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{ width: "20%" }}
          >
            <Alert
              onClose={handleSnackClose}
              severity="success"
              sx={{ width: "100%" }}
            >
              <AlertTitle>Success</AlertTitle>
              Record deleted successfully
            </Alert>
          </Snackbar>
        )}
        {error && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={snackOpen}
            autoHideDuration={3000}
            onClose={handleSnackClose}
            sx={{ width: "20%" }}
          >
            <Alert
              onClose={handleSnackClose}
              severity="error"
              sx={{ width: "100%" }}
            >
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          </Snackbar>
        )}
        {pccSyncMessage && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={snackOpen}
            autoHideDuration={10000}
            onClose={handleSnackClose}
            sx={{ width: "min(90vw, 700px)" }}
          >
            <Alert
              onClose={handleSnackClose}
              severity="success"
              sx={{ width: "100%", textAlign: "center" }}
            >
              <AlertTitle sx={{ textAlign: "center" }}>Alert</AlertTitle>
              {pccSyncMessage}
            </Alert>
          </Snackbar>
        )}
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={Boolean(downloadError)}
          autoHideDuration={4000}
          onClose={handleDownloadSnackClose}
          sx={{ width: "20%" }}
        >
          <Alert
            onClose={handleDownloadSnackClose}
            severity="error"
            sx={{ width: "100%" }}
          >
            <AlertTitle>Error</AlertTitle>
            {downloadError}
          </Alert>
        </Snackbar>
        <Dialog
          open={dialogOpen}
          onClose={handleDialogOpen}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Opps!</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this customer? If you delete the
              customer, their HX rules will also be deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>No</Button>
            <Button
              onClick={() => {
                dispatch(deleteCustomer(deleteCustomerId)).then(() => {
                  dispatch(
                    getCustomer(
                      appliedSearch.value,
                      appliedSearch.type,
                      page + 1,
                      rowsPerPage
                    )
                  );
                  setSnackOpen(true);
                  handleDialogClose();
                });
              }}
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </React.Fragment>
  );
}
